"use client";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ForecastDayRow from "@/app/(pages)/Home/components/desktop/WeekForecast/components/ForecastDayRow";
import { WeatherForecastData } from "@/types/forecast";
import { useSubscription } from "@/providers/WebSocketProvider";
import { ACTIVE_DAY } from "@/app/(pages)/Home/constants/shared";
import dayjs from "@/utils/dayjs";
import { getCityClockFromWeather } from "@/utils/cityTime";

const WeekForecastList = ({
  forecast,
  currentDay,
}: {
  forecast: WeatherForecastData;
  currentDay: string;
}) => {
  const { t, i18n } = useTranslation();
  const [selectedDay, setIsSelected] = useState(currentDay);

  useSubscription(ACTIVE_DAY, (value) => {
    setIsSelected(value);
  });

  const { todayKey } = getCityClockFromWeather(forecast);
  const days = Object.values(forecast).filter((d) => d.date >= todayKey);
  const range =
    days.length > 1
      ? `${dayjs(days[0].date).locale(i18n.language).format("D")}–${dayjs(
          days[days.length - 1].date,
        )
          .locale(i18n.language)
          .format("D MMMM")}`
      : "";

  return (
    <>
      <div className="relative z-50 mb-3 flex items-baseline gap-2.5">
        <h3 className="text-[15px] font-bold tracking-wide">
          {t("week_forecast.label")}
        </h3>
        <span className="text-xs font-medium text-white/40">{range}</span>
      </div>
      <ul className="relative z-10 flex h-full w-full flex-col justify-between gap-1">
        {days.map((day) => (
          <ForecastDayRow
            key={day.date}
            day={day}
            selectedDay={selectedDay}
            t={t}
            i18n={i18n}
          />
        ))}
      </ul>
      <div
        aria-hidden
        className="bg-neon-violet pointer-events-none absolute inset-6 h-4 w-52 rounded-full blur-[60px]"
      />
      <div
        aria-hidden
        className="bg-vivid-blue/30 pointer-events-none absolute top-16 left-[12rem] h-10/12 w-[5rem] -translate-x-2/2 rounded-full blur-[60px] 2xl:left-[12.5rem]"
      />
      <div
        aria-hidden
        className="bg-vivid-blue/30 pointer-events-none absolute right-0 -bottom-6 h-48 w-32 rounded-full blur-[60px]"
      />
    </>
  );
};

export default WeekForecastList;
